import Grid from '@mui/material/Grid';
import type { SxProps } from '@mui/material/styles';
import { TextField } from '@openx/components-core/lib/TextField/TextField';
import type { ContextualSegmentsDrawerState } from '@openx/types/targeting/advancedTargeting';
import type { CustomVariablesDataPayload } from '@openx/types/targeting/customVariables';
import { ComparisonType } from '@openx/types/targeting/targetingValuesTypes';
import { type KeyboardEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { removeNotAllowedSymbols } from '../../ContextualSegments/helpers';
import { DrawerTopBox } from '../../shared/TargetingDrawer/DrawerTopBox';

export interface ContextualSegmentsTopBoxProps {
  items: ContextualSegmentsDrawerState;
  onEnter: (contextual: CustomVariablesDataPayload[]) => void;
  sx: SxProps;
  /** Narrows the box to fit next to the targeting type selector. */
  besideTypeSelector?: boolean;
}

interface ErrorText {
  providerName: string | null;
}

export const ContextualSegmentsTopBox = ({
  items,
  onEnter,
  sx,
  besideTypeSelector = false,
}: ContextualSegmentsTopBoxProps) => {
  const { t } = useTranslation();

  const [providerNameInput, setProviderNameInput] = useState<string>('');
  const [errorText, setErrorText] = useState<ErrorText>({ providerName: null });
  const defaultProviderName = items.contextual[0]?.attr;

  const onKeyPress = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        const attr = defaultProviderName ?? removeNotAllowedSymbols(providerNameInput);

        if (attr === '') {
          const attrError = attr === '' ? t('This field cannot be empty.') : null;

          setErrorText({
            providerName: attrError,
          });
          return;
        }

        onEnter([{ attr, op: ComparisonType.INTERSECTS, val: '' }]);
      }

      setErrorText({ providerName: null });
    },
    [onEnter, t, providerNameInput, defaultProviderName]
  );

  const isLimitReached = !!defaultProviderName;

  return (
    <Grid size={besideTypeSelector ? 8 : 12} sx={besideTypeSelector ? { paddingLeft: '0.8rem' } : undefined}>
      <DrawerTopBox sx={sx}>
        <TextField
          label={t('Provider name')}
          fullWidth
          value={defaultProviderName ?? providerNameInput}
          onChange={e => {
            setProviderNameInput(e.target.value);
          }}
          onKeyUp={onKeyPress}
          placeholder={t('Enter a provider name and press Enter')}
          error={!!errorText.providerName}
          helperText={errorText.providerName}
          data-test="contextual-segments-provider-name-input"
          disabled={isLimitReached}
        />
      </DrawerTopBox>
    </Grid>
  );
};
